import { useEffect } from "react";
import { Sparkles, Lock, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import {
  trackGuestUpgradePromptShown,
  trackGuestUpgradeDismissed,
} from "../lib/guestSession";

/**
 * Hard-block upgrade modal shown when a guest finishes Task 4 of the
 * dial_down_tension practice. Cannot be dismissed back into the
 * practice — the two exits are "Create free account" (primary) or
 * "Back to home" (secondary, sends them to the marketing page).
 *
 * Includes a "What's next" teaser so the guest sees the two tasks that
 * sit right past the wall — lifts sign-up intent by making the value
 * concrete instead of abstract.
 *
 * Fires GA4 `guest_upgrade_prompt_shown` on mount and either
 * `guest_upgrade_signed_up` (fired later in the auth migrate flow) or
 * `guest_upgrade_dismissed` here on the secondary exit.
 */
export const GuestUpgradeModal = ({ onSignUp, onDismiss }) => {
  useEffect(() => {
    trackGuestUpgradePromptShown();
  }, []);

  const handleDismiss = () => {
    trackGuestUpgradeDismissed();
    onDismiss?.();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="guest-upgrade-title"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#1B2349]/80 backdrop-blur-sm px-4 py-8 overflow-y-auto"
      data-testid="guest-upgrade-modal"
    >
      <div className="relative w-full max-w-md bg-[#FBF8F1] rounded-3xl shadow-2xl p-7 sm:p-9 my-auto">
        <div className="flex justify-center mb-5">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#F6C071] to-[#E29B4C] flex items-center justify-center">
            <Sparkles className="w-7 h-7 text-white" />
          </div>
        </div>
        <h2
          id="guest-upgrade-title"
          className="font-outfit font-semibold text-2xl sm:text-3xl text-[#1B2349] text-center mb-3"
          data-testid="guest-upgrade-title"
        >
          You've felt the shift.
        </h2>
        <p className="text-[#4A5568] text-base leading-relaxed text-center mb-5">
          You just completed four tasks of a real bilateral practice — the same kind therapists use in a clinic. There's a lot more waiting for you.
        </p>

        {/* ── What's next teaser ─────────────────────────────────────
              Concrete preview of the two tasks the guest is about to
              unlock. Copy is intentionally sensory (soften, dissolve)
              rather than clinical — this is the "moment of truth" and
              the modal is competing with a browser tab close. */}
        <div
          className="rounded-2xl bg-white/70 border border-[#3090A8]/15 p-4 mb-6"
          data-testid="guest-upgrade-teaser"
        >
          <div className="text-[10px] tracking-widest uppercase text-[#3090A8] font-semibold mb-3">
            Next up · signed-in only
          </div>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-full bg-[#3090A8]/12 text-[#1B2349] text-xs font-semibold flex items-center justify-center flex-shrink-0">5</div>
              <div>
                <div className="text-sm font-semibold text-[#1B2349]">Second Pass Through the Story</div>
                <div className="text-xs text-[#4A5568] leading-relaxed">A deeper release — the tension loosens its grip a second time.</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-full bg-[#3090A8]/12 text-[#1B2349] text-xs font-semibold flex items-center justify-center flex-shrink-0">6</div>
              <div>
                <div className="text-sm font-semibold text-[#1B2349]">Creating a Deeper Calm</div>
                <div className="text-xs text-[#4A5568] leading-relaxed">Replace the old tightness with warmth, softness, and ease.</div>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1 text-xs text-[#718096]">
              <ArrowRight className="w-3.5 h-3.5" />
              <span>Plus three more to finish this practice — and 19 others.</span>
            </div>
          </div>
        </div>

        <ul className="space-y-2 mb-6 text-sm text-[#4A5568]">
          <li className="flex items-start gap-3">
            <Lock className="w-4 h-4 text-[#3090A8] mt-0.5 flex-shrink-0" />
            <span>Free 5-day trial. No card required.</span>
          </li>
        </ul>
        <Button
          onClick={onSignUp}
          className="w-full rounded-full bg-[#1B2349] text-white hover:bg-[#0F1838] h-12 text-base font-medium"
          data-testid="guest-upgrade-signup-btn"
        >
          Create free account
        </Button>
        <button
          type="button"
          onClick={handleDismiss}
          className="w-full mt-3 text-sm text-[#718096] hover:text-[#1B2349] py-2"
          data-testid="guest-upgrade-dismiss-btn"
        >
          Back to home
        </button>
      </div>
    </div>
  );
};

export default GuestUpgradeModal;
